import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/App";
import TopNav from "@/components/TopNav";
import SubscriptionBadge from "@/components/SubscriptionBadge";
import {
  ArrowLeft, CreditCard, Settings, Users, Building2, Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function CorporateSettings() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!auth?.user) return;
    fetchSettings();
  }, [auth?.user]);

  const fetchSettings = async () => {
    try {
      const [subRes, usersRes] = await Promise.all([
        axios.get(`${API}/tenant/admin/subscription`, { headers: getHeaders() }),
        axios.get(`${API}/tenant/admin/users`, { headers: getHeaders() }),
      ]);
      setSubscription(subRes.data.subscription);
      setCurrentPlan(subRes.data.plan);
      setUsers(usersRes.data.users || []);
    } catch (err) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <TopNav />
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <h1 className="text-2xl font-bold mb-6">Corporate Settings</h1>

        <Tabs defaultValue="plan">
          <TabsList>
            <TabsTrigger value="plan"><CreditCard className="w-4 h-4 mr-2" />Plan</TabsTrigger>
            <TabsTrigger value="users"><Users className="w-4 h-4 mr-2" />Members</TabsTrigger>
          </TabsList>

          <TabsContent value="plan" className="mt-4 space-y-4">
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">Current Subscription</h2>
              {subscription ? (
                <div className="space-y-3">
                  <SubscriptionBadge status={subscription.status} planName={currentPlan?.name} />
                  <p className="text-sm text-muted-foreground">
                    {users.length} team member{users.length !== 1 ? "s" : ""}
                  </p>
                  <Button asChild>
                    <Link to="/corporate-hub/settings/billing">
                      <CreditCard className="w-4 h-4 mr-2" />
                      Manage Billing
                    </Link>
                  </Button>
                </div>
              ) : (
                <p className="text-muted-foreground">No active subscription</p>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="users" className="mt-4">
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">Team Members ({users.length})</h2>
              {users.length === 0 ? (
                <p className="text-muted-foreground">No users in this tenant.</p>
              ) : (
                <div className="space-y-2">
                  {users.map(u => (
                    <div key={u.user_id} className="flex items-center justify-between p-3 rounded border">
                      <div>
                        <p className="font-medium text-sm">{u.name || u.email}</p>
                        <p className="text-xs text-muted-foreground">{u.email} · {u.role}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
